import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Anchor, BookOpenText, ChevronRight, Compass, Globe } from "lucide-react";
import axios from "axios";
import { API } from "../lib/api";
import { mediaUrl } from "../components/story/StoryShared";
import { SPECIALS } from "../content/specials";

/* TravelerPulse Specials — editorial features + published traveler stories, browsable by anyone. */
export default function Specials() {
  const [stories, setStories] = useState([]);
  useEffect(() => {
    document.title = "TravelerPulse Specials — stories worth slowing down for";
    axios.get(`${API}/stories/feed/public`).then((r) => setStories(r.data.items)).catch(() => {});
  }, []);

  return (
    <div data-testid="specials-page">
      <div className="bg-gradient-to-br from-zinc-900 to-slate-800 text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 py-12">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-300 flex items-center gap-1.5 mb-2"><Anchor className="w-4 h-4" /> Editorial</div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl">TravelerPulse Specials</h1>
          <p className="text-zinc-300 mt-2 max-w-2xl text-base md:text-lg">Long-form features about places we love — and travel stories published by real travelers from their private diaries.</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-10 space-y-12">
        <section>
          <h2 className="font-heading font-900 text-2xl mb-4 flex items-center gap-2"><Compass className="w-6 h-6 text-emerald-600" /> Special Articles</h2>
          <div className={`grid gap-5 ${SPECIALS.length > 1 ? "md:grid-cols-2" : "max-w-2xl"}`}>
            {SPECIALS.map((s) => (
              <Link key={s.slug} to={s.to} data-testid={`special-card-${s.slug}`}
                className="group border border-zinc-300 bg-white hover:border-emerald-600 transition-colors flex flex-col">
                <div className={`bg-gradient-to-br ${s.accent} text-white p-6`}>
                  <div className="text-[11px] font-mono uppercase tracking-widest opacity-75">{s.place}</div>
                  <div className="font-heading font-900 text-2xl mt-1 leading-tight">{s.title}</div>
                  <div className="text-sm opacity-90 mt-1">{s.subtitle}</div>
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <p className="text-sm text-zinc-600 leading-relaxed">{s.blurb}</p>
                  <span className="mt-4 text-sm font-700 text-emerald-700 inline-flex items-center gap-1 group-hover:underline">Read "{s.heading}" <ChevronRight className="w-4 h-4" /></span>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading font-900 text-2xl mb-1 flex items-center gap-2"><BookOpenText className="w-6 h-6 text-fuchsia-600" /> Traveler Stories</h2>
          <p className="text-sm text-muted-foreground mb-4">Published by travelers from their own Travel Diaries — privacy-shielded, always with their explicit approval. Create yours from any trip's diary in <Link to="/travel-hub" className="text-primary hover:underline">My Travel Hub</Link>.</p>
          {stories.length === 0 ? (
            <p className="text-sm text-muted-foreground border border-dashed border-zinc-300 p-8 text-center" data-testid="specials-stories-empty">No public traveler stories yet — be the first: open a trip's Traveler Diary and press "Create Travel Story".</p>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="specials-stories-grid">
              {stories.map((s) => (
                <a key={s.public_id} href={`/story/${s.public_id}`} data-testid={`feed-story-${s.public_id}`}
                  className="group border border-zinc-300 bg-white hover:border-fuchsia-500 transition-colors flex flex-col">
                  {s.cover_media_id
                    ? <img src={mediaUrl(s.id, s.cover_media_id)} alt="" className="w-full h-40 object-cover" loading="lazy" />
                    : <div className="w-full h-40 bg-gradient-to-br from-fuchsia-100 to-indigo-100 flex items-center justify-center text-fuchsia-300"><Globe className="w-10 h-10" /></div>}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="font-heading font-900 text-base leading-snug group-hover:underline">{s.title}</h3>
                    {s.subtitle && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.subtitle}</p>}
                    <div className="mt-auto pt-3 text-[11px] text-muted-foreground">
                      By {s.author_display || "Anonymous Traveler"}{s.date_display ? ` · ${s.date_display}` : ""}{s.language ? ` · ${s.language}` : ""} · {s.views || 0} view{s.views === 1 ? "" : "s"}
                    </div>
                  </div>
                </a>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
